import React from 'react'
import Image from 'next/image'
import { SlimLoadingSkeletonWFull } from 'components/loading-skeleton-single-card-tile'
import { nextImageHelper, useFetchCardsSlug } from '../../utils'
import { generateNewOrderId } from '../../utils/orderId/generateNewOrderId'
import { useMemo, useState } from 'react'
import { SummaryRowGro925 } from 'components/tips-summary'

/**
 * 2026.06 - This component will be used on a landing page test for Social for
 * GRO-925. If it doesn't work out we'll need to delete it. If it does work,
 * we'll likely want to refactor it (e.x. to get rid of type assertions)
 *
 * @see https://github.com/compare-credit/comparecredit/pull/5843#discussion_r2150621660
 */
export const StaticHeaderTable = () => {
  const orderIds = useMemo(
    () => ({
      doubleCash: generateNewOrderId(),
      reflect: generateNewOrderId(),
      travelRewards: generateNewOrderId(),
      freedomUnlimited: generateNewOrderId(),
    }),
    [],
  )

  const [summaryToggle, setSummaryToggle] = useState(true)
  const handleSummaryToggle = () => setSummaryToggle(!summaryToggle)

  const categoryId = '17' // Cash Back
  const externalId = '17'

  const sortParams = { categoryId: externalId }

  const slugs = [
    'double-cash',
    'reflect',
    'travel-rewards',
    'freedom-unlimited',
  ]

  const { cards, isFetching } = useFetchCardsSlug(slugs, sortParams)

  const cardDoubleCash = cards.filter((card) => card.slug === 'double-cash')[0]
  const cardDoubleCashId = 'card-double-cash'
  const cardDoubleCashHighlight = 'Cash Back + Intro BT Offer'

  const cardReflect = cards.filter((card) => card.slug === 'reflect')[0]
  const cardReflectId = 'card-reflect'
  const cardReflectHighlight = 'Top Intro APR Offer'

  const cardTravelRewards = cards.filter(
    (card) => card.slug === 'travel-rewards',
  )[0]
  const cardTravelRewardsId = 'card-travel-rewards'
  const cardTravelRewardsHighlight = 'Big Welcome Offer + Bonus Points'

  const cardFreedomUnlimited = cards.filter(
    (card) => card.slug === 'freedom-unlimited',
  )[0]
  const cardFreedomUnlimitedId = 'card-freedom-unlimited'
  const cardFreedomUnlimitedHighlight = 'Unlimited Cash Rewards'

  return (
    <div className={`c-table`}>
      <header className="relative / md:mb-1 / border-b-4 border-tetriary">
        <h1
          className={`c-title flex flex-row justify-center / mb-3 lg:mb-3.5 / u-sup /
                  text-primary-mid leading-[1.2] font-serif text-primary font-bold / text-[32px] xs:text-[36px] sm:text-[42px] md:text-[48px]`}
        >
          <span className="inline-block shrink-0 w-14 md:w-20 sm:pt-2 md:pt-3">
            <Image
              alt="checkmark"
              src="/static/icons/checkmark-circle.svg"
              height={24}
              width={24}
              aria-hidden={true}
              style={nextImageHelper.responsive}
              sizes="100vw"
            />
          </span>{' '}
          <span className="flex flex-col pl-2 sm:pl-4">
            <span>Buzz Worthy Cards</span>{' '}
            <span className="font-sans text-[16px] xs:text-[18px] sm:text-[21px] md:text-[24px]">
              With 0% Interest Until Nearly&nbsp;2027
            </span>
          </span>
        </h1>
      </header>

      <section className="c-summary / w-full / mb-6">
        <div
          id="our-pick-summary"
          className={`c-summary__table c-slidedown / relative / w-full / mb-3 / transition-25s ${
            summaryToggle
              ? ''
              : 'c-slidedown--hidden min-h-[5rem] max-h-[5rem] opacity-100'
          }`}
        >
          {isFetching && (
            <>
              <SlimLoadingSkeletonWFull n={3} />
            </>
          )}
          {!isFetching &&
            orderIds &&
            cardDoubleCash &&
            cardDoubleCash._type === 'card' && (
              <div className="relative z-4">
                <SummaryRowGro925
                  card={cardDoubleCash}
                  categoryId={categoryId}
                  externalId={externalId}
                  dataId={cardDoubleCashId}
                  iconWidth={'w-5'}
                  orderId={orderIds.doubleCash}
                  productHighlight={cardDoubleCashHighlight}
                  ribbonColor="tetriary"
                />
              </div>
            )}
          {!isFetching &&
            orderIds &&
            cardReflect &&
            cardReflect._type === 'card' && (
              <div className="relative z-3">
                <SummaryRowGro925
                  card={cardReflect}
                  categoryId={categoryId}
                  externalId={externalId}
                  dataId={cardReflectId}
                  iconWidth={'w-5'}
                  orderId={orderIds.reflect}
                  productHighlight={cardReflectHighlight}
                  ribbonColor="blue"
                />
              </div>
            )}
          {!isFetching &&
            orderIds &&
            cardTravelRewards &&
            cardTravelRewards._type === 'card' && (
              <div className="relative z-2">
                <SummaryRowGro925
                  card={cardTravelRewards}
                  categoryId={categoryId}
                  externalId={externalId}
                  dataId={cardTravelRewardsId}
                  iconWidth={'w-5'}
                  orderId={orderIds.travelRewards}
                  productHighlight={cardTravelRewardsHighlight}
                />
              </div>
            )}
          {!isFetching &&
            orderIds &&
            cardFreedomUnlimited &&
            cardFreedomUnlimited._type === 'card' && (
              <div className="relative z-1">
                <SummaryRowGro925
                  card={cardFreedomUnlimited}
                  categoryId={categoryId}
                  externalId={externalId}
                  dataId={cardFreedomUnlimitedId}
                  iconWidth={'w-5'}
                  orderId={orderIds.freedomUnlimited}
                  productHighlight={cardFreedomUnlimitedHighlight}
                />
              </div>
            )}
          <div
            className={`c-summary__bg-gradient / absolute z-5 / bottom-0 left-0 / w-full h-1/3 / bg-gradient-to-t from-white / opacity-90 ${
              summaryToggle ? 'hidden' : null
            }`}
          ></div>
        </div>
        <button
          className="flex flex-row items-center / text-sm text-primary-bright-dark font-semibold / hover:text-primary-bright hover:underline"
          onClick={handleSummaryToggle}
          aria-label={`${summaryToggle ? 'Show' : 'Hide'} summary`}
          aria-expanded={summaryToggle ? false : true}
          aria-controls="our-pick-summary"
        >
          {summaryToggle ? 'Hide' : 'Show'} Summary{' '}
          {summaryToggle ? (
            <span className="inline-block w-4 ml-1">
              <Image
                alt="expand"
                src="/static/icons/icon-minus-primary-bright.svg"
                height={24}
                width={24}
                aria-hidden={true}
                style={nextImageHelper.responsive}
                sizes="100vw"
              />
            </span>
          ) : (
            <span className="inline-block w-4 ml-1">
              <Image
                alt="expand"
                src="/static/icons/icon-plus-primary-bright.svg"
                height={24}
                width={24}
                aria-hidden={true}
                style={nextImageHelper.responsive}
                sizes="100vw"
              />
            </span>
          )}
        </button>
      </section>
    </div>
  )
}
